import React from 'react';
import { MapPin, Phone, MessageCircle } from 'lucide-react';
import { contactInfo } from '../utils/mockData';

export const ContactSection = () => {
  const openWhatsApp = (number, location) => {
    const message = encodeURIComponent(`Bonjour, je vous contacte depuis le site Rapide Colis concernant ${location}`);
    window.open(`https://wa.me/${number.replace(/\s/g, '')}?text=${message}`, '_blank');
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#204FA7] mb-4">
            Contactez-nous
          </h2>
          <p className="text-lg text-gray-600">
            Notre équipe est à votre disposition pour répondre à toutes vos questions
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Paris Contact */}
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 border-2 border-[#204FA7]/20 shadow-lg">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-[#204FA7] rounded-full flex items-center justify-center">
                <MapPin className="text-white" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Bureau France</p>
                <h3 className="text-2xl font-bold text-[#204FA7]">Paris</h3>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex items-start space-x-3">
                <MapPin className="text-[#204FA7] flex-shrink-0 mt-1" size={20} />
                <div>
                  <p className="font-semibold text-gray-900">{contactInfo.paris.address}</p>
                  <p className="text-gray-600">{contactInfo.paris.details}</p>
                  <p className="text-gray-600">{contactInfo.paris.postalCode}</p>
                </div>
              </div>
            </div>

            <div className="space-y-3 pt-6 border-t border-gray-200">
              <p className="text-sm font-semibold text-gray-700 mb-3">Contactez-nous sur WhatsApp :</p>
              {contactInfo.paris.whatsapp.map((number, index) => (
                <button
                  key={index}
                  onClick={() => openWhatsApp(number, 'Paris')}
                  className="w-full flex items-center justify-between bg-white border-2 border-[#204FA7] text-[#204FA7] px-4 py-3 rounded-lg hover:bg-[#204FA7] hover:text-white transition-all group"
                >
                  <div className="flex items-center space-x-3">
                    <MessageCircle size={20} />
                    <span className="font-medium">{number}</span>
                  </div>
                  <span className="text-sm opacity-75 group-hover:opacity-100">Ouvrir WhatsApp</span>
                </button>
              ))}
            </div>
          </div>

          {/* Abidjan Contacts */}
          <div className="bg-gradient-to-br from-orange-50 to-white rounded-2xl p-8 border-2 border-[#F3A530]/20 shadow-lg">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-[#F3A530] rounded-full flex items-center justify-center">
                <MapPin className="text-white" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Bureaux Côte d'Ivoire</p>
                <h3 className="text-2xl font-bold text-[#F3A530]">Abidjan</h3>
              </div>
            </div>

            <div className="space-y-4">
              {contactInfo.abidjan.map((office, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl p-4 border border-gray-200 hover:border-[#F3A530] hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start space-x-2 flex-1">
                      <MapPin className="text-[#F3A530] flex-shrink-0 mt-1" size={18} />
                      <p className="font-semibold text-gray-900">{office.location}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => openWhatsApp(office.whatsapp, office.location)}
                    className="w-full flex items-center justify-between bg-[#F3A530] text-white px-4 py-2 rounded-lg hover:bg-[#e09620] transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <MessageCircle size={18} />
                      <span className="font-medium">{office.whatsapp}</span>
                    </div>
                    <span className="text-sm">WhatsApp</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-[#204FA7] to-[#1CA1CC] rounded-2xl p-8 sm:p-12 text-white shadow-xl">
          <MessageCircle className="mx-auto mb-4" size={48} />
          <h3 className="text-2xl sm:text-3xl font-bold mb-4">
            Service client disponible 7j/7
          </h3>
          <p className="text-lg mb-8 text-white/90 max-w-2xl mx-auto">
            Notre équipe est prête à répondre à toutes vos questions et vous accompagner dans l'envoi de vos colis
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://wa.me/33695701735?text=Bonjour,%20j'ai%20une%20question"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center space-x-2 bg-white text-[#204FA7] px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors font-semibold shadow-lg"
            >
              <MessageCircle size={20} />
              <span>WhatsApp Paris</span>
            </a>
            <a 
              href="https://wa.me/2250713190304?text=Bonjour,%20j'ai%20une%20question"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center space-x-2 bg-white text-[#F3A530] px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors font-semibold shadow-lg"
            >
              <MessageCircle size={20} />
              <span>WhatsApp Abidjan</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
